import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/requests';
import log from '../../logger';

// Fetch trends
export const fetchTrends = createAsyncThunk(
  'trend/fetchTrends',
  async ({ page = 1, limit = 10, filters = {}, categoryId } = {}, { rejectWithValue }) => {
    try {
      log.info('Fetching trends with page:', page, 'and limit:', limit);
      const params = { page, limit, ...filters };
      if (categoryId) params.categoryId = categoryId;
      const response = await api.trends.getAll(params);
      if (response.data.status) {
        log.info('Trends fetched successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to fetch trends:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error fetching trends:', error);
      return rejectWithValue(error.response?.data?.message || 'Error fetching trends');
    }
  }
);

// Search trends with keyword
export const searchTrends = createAsyncThunk(
  'trend/searchTrends',
  async ({ keyword, page = 1, limit = 10, filters = {} } = {}, { rejectWithValue }) => {
    try {
      log.info('Searching trends with keyword:', keyword);
      const response = await api.trends.search({ keyword, page, limit, ...filters });
      if (response.data.status) {
        log.info('Trends search completed successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to search trends:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error searching trends:', error);
      return rejectWithValue(error.response?.data?.message || 'Error searching trends');
    }
  }
);

// Create trend
export const createTrend = createAsyncThunk(
  'trend/createTrend',
  async (formData, { rejectWithValue }) => {
    try {
      log.info('Creating trend with data:', formData);
      const response = await api.trends.create(formData);
      if (response.data.status) {
        log.info('Trend created successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to create trend:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error creating trend:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to create trend');
    }
  }
);

// Update trend
export const updateTrend = createAsyncThunk(
  'trend/updateTrend',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      log.info('Updating trend with ID:', id, 'and data:', formData);
      const response = await api.trends.update(id, formData);
      if (response.data.status) {
        log.info('Trend updated successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to update trend:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error updating trend:', error);
      return rejectWithValue(error.response?.data?.message || 'Error updating trend');
    }
  }
);

// Delete trend
export const deleteTrend = createAsyncThunk(
  'trend/deleteTrend',
  async (id, { rejectWithValue }) => {
    try {
      log.info('Deleting trend with ID:', id);
      const response = await api.trends.delete(id);
      if (response.data.status) {
        log.info('Trend deleted successfully:', id);
        return id;
      } else {
        log.warn('Failed to delete trend:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error deleting trend:', error);
      return rejectWithValue(error.response?.data?.message || 'Error deleting trend');
    }
  }
);

// Bulk delete trends
export const bulkDeleteTrends = createAsyncThunk(
  'trend/bulkDeleteTrends',
  async (ids, { rejectWithValue }) => {
    try {
      await Promise.all(ids.map(id => api.trends.delete(id)));
      return ids;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error deleting trends');
    }
  }
);

// Bulk activate/deactivate trends
export const bulkUpdateTrendStatus = createAsyncThunk(
  'trend/bulkUpdateStatus',
  async ({ ids, status }, { rejectWithValue }) => {
    try {
      await api.trends.bulkUpdateStatus(ids, status);
      return { ids, status };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error updating trend status');
    }
  }
);

// Fetch a single trend by ID
export const fetchTrendById = createAsyncThunk(
  'trend/fetchTrendById',
  async (id, { rejectWithValue }) => {
    try {
      log.info('Fetching trend with ID:', id);
      const response = await api.trends.getById(id);
      if (response.data.status) {
        log.info('Trend fetched successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to fetch trend:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error fetching trend:', error);
      return rejectWithValue(error.response?.data?.message || 'Error fetching trend');
    }
  }
);

// Update trend orders
export const updateTrendOrders = createAsyncThunk(
  'trend/updateTrendOrders',
  async (trends, { rejectWithValue }) => {
    try {
      log.info('Updating trend orders:', trends);
      const response = await api.trends.updateOrder({ trends });
      if (response.data.status) {
        log.info('Trend orders updated successfully');
        return trends;
      } else {
        log.warn('Failed to update trend orders:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error updating trend orders:', error);
      return rejectWithValue(error.response?.data?.message || 'Error updating trend orders');
    }
  }
);

const initialState = {
  items: [],
  total: 0,
  page: 1,
  pages: 0,
  limit: 10,
  loading: false,
  error: null,
  orderUpdateLoading: false,
  orderUpdateError: null
};

const trendSlice = createSlice({
  name: 'trend',
  initialState,
  reducers: {
    reorderTrends: (state, action) => {
      const { oldIndex, newIndex } = action.payload;
      const items = [...state.items];
      const [movedItem] = items.splice(oldIndex, 1);
      items.splice(newIndex, 0, movedItem);
      state.items = items;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch trends
      .addCase(fetchTrends.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTrends.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = action.payload.trends;
        state.total = parseInt(action.payload.total);
        state.page = parseInt(action.payload.page);
        state.pages = parseInt(action.payload.pages);
        state.limit = parseInt(action.payload.limit);
      })
      .addCase(fetchTrends.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Search trends
      .addCase(searchTrends.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchTrends.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = action.payload.trends || [];
        state.total = parseInt(action.payload.total || 0);
        state.page = parseInt(action.payload.page || 1);
        state.pages = parseInt(action.payload.pages || 0);
        state.limit = parseInt(action.payload.limit || 10);
      })
      .addCase(searchTrends.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create trend
      .addCase(createTrend.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTrend.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items.push(action.payload);
      })
      .addCase(createTrend.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update trend
      .addCase(updateTrend.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTrend.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const index = state.items.findIndex(item => item._id === action.payload._id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(updateTrend.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete trend
      .addCase(deleteTrend.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTrend.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = state.items.filter(item => item._id !== action.payload);
      })
      .addCase(deleteTrend.rejected, (state) => {
        state.loading = false;
      })
      // Bulk delete trends
      .addCase(bulkDeleteTrends.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkDeleteTrends.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(bulkDeleteTrends.rejected, (state) => {
        state.loading = false;
      })
      .addCase(bulkUpdateTrendStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkUpdateTrendStatus.fulfilled, (state, action) => {
        state.loading = false;
        const { ids, status } = action.payload;
        state.items = state.items.map(item => ids.includes(item._id) ? { ...item, status } : item);
      })
      .addCase(bulkUpdateTrendStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch a single trend by ID
      .addCase(fetchTrendById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTrendById.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = [action.payload]; // Assuming we want to replace the current items with the fetched one
      })
      .addCase(fetchTrendById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update trend orders
      .addCase(updateTrendOrders.pending, (state) => {
        state.orderUpdateLoading = true;
        state.orderUpdateError = null;
      })
      .addCase(updateTrendOrders.fulfilled, (state) => {
        state.orderUpdateLoading = false;
        state.orderUpdateError = null;
      })
      .addCase(updateTrendOrders.rejected, (state, action) => {
        state.orderUpdateLoading = false;
        state.orderUpdateError = action.payload;
      });
  },
});

export default trendSlice.reducer; 